import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import IndoorPositioningHistory from "../../../../api/IndoorPositioningHistory";

const isAbort = (error) =>
  error?.name === "CanceledError" || error?.name === "AbortError";

const PAGE_SIZE = 50;

// Keyset-paginated alert history for the current range, OLDEST FIRST so the
// list reads in replay order. Never uses OFFSET (per the API contract): each
// "載入更多" click passes the last page's `nextAfterId` back as `after_id`.
export const useHistoryAlerts = (machineID, range) => {
  const [alerts, setAlerts] = useState([]);
  const [nextAfterId, setNextAfterId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const abortRef = useRef(null);

  const fetchPage = useCallback(
    async (afterId, replace) => {
      if (!machineID || !range) return;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (replace) setLoading(true);
      else setLoadingMore(true);

      try {
        const response = await IndoorPositioningHistory.GetAlerts(
          machineID,
          range.startMs,
          range.endMs,
          afterId,
          PAGE_SIZE,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        if (response.code === 200 && response.data) {
          setAlerts((prev) =>
            replace ? response.data.alerts : [...prev, ...response.data.alerts],
          );
          setNextAfterId(response.data.nextAfterId ?? null);
        } else if (replace) {
          setAlerts([]);
          setNextAfterId(null);
        }
      } catch (error) {
        if (isAbort(error)) return;
        message.error("載入歷史警報失敗");
        if (replace) {
          setAlerts([]);
          setNextAfterId(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          if (replace) setLoading(false);
          else setLoadingMore(false);
        }
      }
    },
    [machineID, range?.startMs, range?.endMs],
  );

  useEffect(() => {
    fetchPage(null, true);
    return () => abortRef.current?.abort();
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (nextAfterId != null) fetchPage(nextAfterId, false);
  }, [nextAfterId, fetchPage]);

  return {
    alerts,
    hasMore: nextAfterId != null,
    loading,
    loadingMore,
    loadMore,
  };
};
